import { useTableUniverse } from './index';
import { v4 as uuid } from 'uuid';
import { useApolloClient, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { Cheque, Item } from '@baemingo/table-events/dist/types';

import { useSelector, useDispatch, useI18n, useCurrency } from '~Hooks';
import * as actions from '~actions';
import { CartItem, LocalItem } from '~Types';
import { DISCOUNT_TYPE } from '~../__queries__/globalTypes';
import TimeManager from '~Components/TimeManager';

interface Table {
  id: string;
  name: string;
}

const TABLE_FRAGMENT = gql`
  fragment TableName on Table {
    id
    code
  }
`;

const ADD_TABLE_LOG = gql`
  mutation AddTableLog($input: TableLogInput!) {
    addTableLog(input: $input) {
      success
    }
  }
`;

const getChequeNamefromId = (id: string) => `#${id?.slice(0, 4).toUpperCase()}`;

const getChequeById = (cheques: Cheque[], id: string): Cheque | undefined => {
  return cheques.find((c) => c.id === id);
};

const getItemById = (items: Item[], id: string): Item | undefined => {
  return items.find((i) => i.id === id);
};

export const useTableLogs = () => {
  const universe = useTableUniverse();
  const staffId = useSelector((state) => state.staff?.id);
  const client = useApolloClient();
  const currency = useCurrency();

  const [addTableLog] = useMutation(ADD_TABLE_LOG);

  function log(options: {
    tableId?: string | null;
    chequeId: string;
    title: string;
    message?: string | null;
    eventId: string;
  }) {
    const { tableId, chequeId, title, message, eventId } = options;

    addTableLog({
      variables: {
        input: {
          staffId,
          tableId,
          chequeId,
          title,
          message,
          eventId,
        },
      },
    });
  }

  const { i18n } = useI18n();
  const Bill = i18n('TM.Bill');
  const Created = i18n('TM.Created');
  const Moved = i18n('TM.Moved');
  const MovedFrom = i18n('TM.MovedFrom');
  const Added = i18n('TM.Added');
  const Renamed = i18n('TM.RenamedTo');
  const Removed = i18n('TM.Removed');
  const Coverage = i18n('TM.Coverage');
  const table = i18n('TM.Table');
  const CoverageChange = i18n('TM.CoverageChange');
  const SplittedTo = i18n('TM.SplittedTo');
  const SplitFrom = i18n('TM.SplitFrom');
  const MergedInto = i18n('TM.MergedInto');
  const DiscountAdded = i18n('TM.DiscountAdded');
  const DiscountReseted = i18n('TM.DiscountReseted');
  const InitiatePaymnet = i18n('TM.InitiatePaymnet');
  const AddPayment = i18n('TM.AddPayment');
  const Printed = i18n('TM.Printed');

  const generateEventMeta = () => ({
    eventId: uuid(),
    createdAt: TimeManager.now().toISOString(),
    staffId,
  });

  const logApi = {
    message: (chequeId: string, tableId: string, message: string) => {
      log({
        tableId,
        chequeId,
        title: i18n('TSR.KitchenMessage'),
        message: `${i18n('TSR.KitchenMessage')}: ${message}`,
        ...generateEventMeta(),
      });
    },
    addItems: (chequeId: string, tableId: string, items: CartItem[]) => {
      const verb = items.length !== 1 ? i18n('TSR.ArticlesBonged') : i18n('TSR.ArticleBonged');
      const count = items.length;

      log({
        tableId,
        chequeId,
        title: `${count} ${verb}`,
        message: items.map((item) => item.title).join(', '),
        ...generateEventMeta(),
      });
    },
    removeItems: (chequeId: string, tableId: string, items: Item[], reason: string) => {
      const verb = items.length !== 1 ? i18n('TSR.ArticlesTrashed') : i18n('TSR.ArticleTrashed');

      log({
        tableId,
        chequeId,
        title: `${items.length} ${verb}`,
        message: `${items.map((item) => item.title).join(', ')}
          
${i18n('Bong.Correction.Comment')}: ${reason ?? '--'}`,

        ...generateEventMeta(),
      });
    },
    changeCourse: (chequeId: string, tableId: string, course: string) => {
      log({
        tableId,
        chequeId,
        title: `${i18n('TSR.NextCourse')}: ${course}`,
        ...generateEventMeta(),
      });
    },
    createCheque: (chequeId: string, tableId: string, coverage: number) => {
      const coverageMsg = coverage ? `- ${coverage} ${Coverage}` : '';
      const message = `${Bill} ${getChequeNamefromId(chequeId)} ${Created} ${coverageMsg}`;

      log({
        tableId,
        chequeId,
        title: message,
        ...generateEventMeta(),
      });
    },

    moveCheque: (id: string, oldTable: Table | null, newTable: Table | null) => {
      // Add logs
      const cheque = getChequeById(universe.cheques, id);
      const chequeName = cheque?.name || getChequeNamefromId(id);

      // Log for old table
      log({
        tableId: oldTable?.id,
        chequeId: id,
        title: `${Bill} ${chequeName} ${Moved} ${table} ${newTable?.name}`,
        ...generateEventMeta(),
      });

      // Log for new table
      log({
        tableId: newTable?.id,
        chequeId: id,
        title: `${Bill} ${chequeName} ${MovedFrom} ${table} ${oldTable?.name}`,
        ...generateEventMeta(),
      });
    },

    removeCheque: (chequeId: string, message: string) => {
      const cheque = getChequeById(universe.cheques, chequeId);
      const tableId = cheque?.tableId;
      const chequeName = cheque?.name || getChequeNamefromId(chequeId);

      const amount = cheque?.amount;
      const discount = cheque?.discount;

      const leftToPay = (amount || 0) - (discount || 0);

      log({
        chequeId,
        tableId,
        title: `${Bill} ${chequeName} ${Removed}`,
        message: `${i18n('Bong.Correction.Comment')}: ${message ?? '--'}
        
${currency.format(leftToPay)}`,
        ...generateEventMeta(),
      });
    },

    setChequeName: (id: string, name: string | null) => {
      const cheque = getChequeById(universe.cheques, id);
      const tableId = cheque?.tableId;
      const chequeName = cheque?.name || getChequeNamefromId(id);

      log({
        tableId,
        chequeId: id,
        title: `${Bill} ${chequeName} ${Renamed} ${name}`,
        ...generateEventMeta(),
      });
    },

    setChequeCoverage: (id: string, coverage: number | null) => {
      const cheque = getChequeById(universe.cheques, id);
      const tableId = cheque?.tableId;
      const chequeName = cheque?.name || getChequeNamefromId(id);

      log({
        tableId,
        chequeId: id,
        title: `${CoverageChange} ${coverage} - ${Bill} ${chequeName}`,
        ...generateEventMeta(),
      });
    },

    splitCheque: (id: string, ids: string[]) => {
      const cheque = getChequeById(universe.cheques, id);
      const originalChequeName = cheque?.name || getChequeNamefromId(id);

      const count = ids.length + 1;

      log({
        tableId: cheque?.tableId,
        chequeId: id,
        title: `${Bill} ${originalChequeName} ${SplittedTo} ${count}`,
        ...generateEventMeta(),
      });

      for (const newId of ids) {
        log({
          chequeId: newId,
          title: `${Bill} ${SplitFrom} ${originalChequeName}`,
          ...generateEventMeta(),
        });
      }
    },

    mergeCheque: (id: string, chequeToMergeIntoId: string) => {
      // Add logs
      const sourceCheque = getChequeById(universe.cheques, id);
      const chequeName = sourceCheque?.name || getChequeNamefromId(id);

      const targetCheque = getChequeById(universe.cheques, chequeToMergeIntoId);
      const targetChequeName = targetCheque?.name || getChequeNamefromId(chequeToMergeIntoId);

      const tableId = targetCheque?.tableId || sourceCheque?.tableId;
      const message = `${Bill} ${chequeName} ${MergedInto} ${targetChequeName}`;

      log({
        chequeId: chequeToMergeIntoId,
        tableId,
        title: message,
        ...generateEventMeta(),
      });
    },

    addItem: (item: LocalItem, chequeId: string) => {
      const cheque = getChequeById(universe.cheques, chequeId);
      const chequeName = cheque?.name || getChequeNamefromId(chequeId);

      log({
        chequeId,
        tableId: cheque?.tableId,
        title: `${item.title} ${Added} ${Bill} ${chequeName}`,
        ...generateEventMeta(),
      });
    },

    moveItemToCheque: (id: string, chequeId: string) => {
      const targetCheque = getChequeById(universe.cheques, chequeId);
      const targertChequeName = targetCheque?.name || getChequeNamefromId(chequeId);

      const item = getItemById(universe.items, id)!;

      const sourcCheque = getChequeById(universe.cheques, item?.chequeId)!;
      const sourceChequeName = sourcCheque?.name || getChequeNamefromId(sourcCheque?.id);

      const tableId = targetCheque?.tableId || sourcCheque?.tableId;

      // Log on source cheque
      log({
        tableId,
        chequeId: sourcCheque?.id,
        title: `${item?.title} ${Moved} ${Bill} ${targertChequeName}`,
        ...generateEventMeta(),
      });

      // Log on target cheque
      log({
        chequeId,
        title: `${item?.title} ${MovedFrom} ${Bill} ${sourceChequeName}`,
        ...generateEventMeta(),
      });
    },

    moveItemsToTable: (ids: string[], chequeId: string, tableId: string) => {
      ids.forEach(async (id) => {
        const item = getItemById(universe.items, id)!;
        const cheque = getChequeById(universe.cheques, item?.chequeId);

        const oldTable = await client.readFragment({
          id: cheque?.tableId || '',
          fragment: TABLE_FRAGMENT,
        });

        const newTable = await client.readFragment({
          id: tableId || '',
          fragment: TABLE_FRAGMENT,
        });

        // Log on the old table
        log({
          tableId: oldTable.id,
          chequeId: item?.chequeId,
          title: `${item?.title} ${Moved} ${table} ${newTable.code}`,
          ...generateEventMeta(),
        });

        // Log on the new table
        log({
          chequeId,
          title: `${item?.title} ${MovedFrom} ${table} ${oldTable.code}`,
          ...generateEventMeta(),
        });
      });
    },

    setDiscounts: (
      items: Array<{
        id: string;
        discount: number;
      }>,
      rate: number,
      type: DISCOUNT_TYPE,
    ) => {
      if (rate < 0) return;

      // Get table id from an item
      const itemId = items[0].id;
      const item = getItemById(universe.items, itemId)!;
      const cheque = getChequeById(universe.cheques, item?.chequeId)!;
      const chequeName = cheque?.name || getChequeNamefromId(cheque?.id);

      const mappedItems = items.map((i) => {
        const item = getItemById(universe.items, i.id);

        if (rate === 0) {
          return item?.title;
        }

        if (!i.discount) {
          return `${item?.title} ${i18n('TM.Skipped')}`;
        }

        return `${item?.title} (${currency.format(i.discount)})`;
      });
      const itemsString = mappedItems.join('\n');

      // get discount
      const discount =
        type === DISCOUNT_TYPE.PERCENTAGE
          ? `${rate}%`
          : type === DISCOUNT_TYPE.PRICE
          ? currency.format(rate)
          : '';

      const message =
        rate === 0
          ? `${DiscountReseted} ${Bill} ${chequeName}`
          : `${discount} ${DiscountAdded} ${Bill} ${chequeName}`;

      // Add log
      log({
        chequeId: cheque?.id,
        tableId: cheque?.tableId,
        title: message,
        message: `${itemsString}`,
        ...generateEventMeta(),
      });
    },

    initiatePayment: (chequeId: string) => {
      const cheque = getChequeById(universe.cheques, chequeId)!;
      const chequeName = cheque.name || getChequeNamefromId(cheque.id);
      log({
        tableId: cheque.tableId,
        chequeId,
        title: `${InitiatePaymnet} ${Bill} ${chequeName}`,
        ...generateEventMeta(),
      });
    },

    addPayment: (
      chequeId: string,
      amount: number,
      paymentType: string,
      tip: number,
      isFullPayment: boolean,
    ) => {
      const cheque = getChequeById(universe.cheques, chequeId);
      const chequeName = cheque?.name || getChequeNamefromId(chequeId);

      const partialOrFull = isFullPayment ? '' : `(${i18n('TM.PartialPayment')})`;

      log({
        tableId: cheque?.tableId,
        chequeId,
        title: `${chequeName} ${i18n('TSR.Logs.Paid')}: ${currency.format(
          amount,
        )} ${partialOrFull}`,
        ...generateEventMeta(),
      });
    },

    markPrinted: (chequeId: string) => {
      const cheque = getChequeById(universe.cheques, chequeId);
      const chequeName = cheque?.name || getChequeNamefromId(chequeId);

      log({
        tableId: cheque?.tableId,
        chequeId,
        title: `${Bill} ${chequeName} ${Printed}`,
        ...generateEventMeta(),
      });
    },
  };

  return logApi;
};
